import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Store,
  ArrowLeft,
  Shield,
  ShieldCheck,
  Zap,
  Wallet,
  ArrowRight,
  Sparkles,
  QrCode,
  TrendingUp,
  CheckCircle2,
  Lock,
  Building2,
  Headphones
} from 'lucide-react';

const LoginPortal: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#faf8ed] text-secondary-900 flex flex-col justify-between relative overflow-hidden font-sans selection:bg-[#0c513f] selection:text-[#faf8ed]">
      
      {/* Dynamic Ambient Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
        {/* Soft Glowing Gradient Mesh Blobs */}
        <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-[#0c513f]/10 blur-[130px] animate-blob" />
        <div className="absolute top-[30%] right-[-10%] w-[550px] h-[550px] rounded-full bg-chowdeck-pastelYellow/40 blur-[140px] animate-blob animation-delay-2000" />
        <div className="absolute bottom-[-10%] left-[20%] w-[650px] h-[650px] rounded-full bg-chowdeck-pastelPurple/25 blur-[150px] animate-blob animation-delay-4000" />

        {/* Floating Decorative Brand Elements */}
        <div className="absolute top-24 left-[8%] opacity-15 animate-float">
          <QrCode size={90} className="text-[#0c513f] rotate-12" />
        </div>
        <div className="absolute top-44 right-[8%] opacity-15 animate-float animation-delay-2000">
          <Wallet size={100} className="text-[#0c513f] -rotate-12" />
        </div>
        <div className="absolute bottom-28 left-[10%] opacity-15 animate-float animation-delay-4000">
          <Store size={95} className="text-[#0c513f] rotate-6" />
        </div>
        <div className="absolute bottom-36 right-[12%] opacity-15 animate-float">
          <ShieldCheck size={110} className="text-[#0c513f] -rotate-6" />
        </div>
      </div>

      {/* Top Navigation Bar */}
      <header className="w-full max-w-7xl mx-auto px-6 py-6 sm:py-8 flex items-center justify-between z-20">
        <Link to="/" className="flex items-center space-x-3 group">
          <img
            src="/rita_logo_2-removebg-preview.png"
            alt="Rita Logo"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Security Status Indicator */}
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-[#0c513f]/10 text-xs font-semibold text-[#0c513f] shadow-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>256-Bit SSL Encrypted</span>
          </div>

          {/* Back to Home Button */}
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/90 hover:bg-white text-secondary-800 hover:text-[#0c513f] text-sm font-bold border border-secondary-200/80 hover:border-[#0c513f]/30 shadow-sm transition-all duration-300 hover:shadow hover:scale-105 active:scale-95"
          >
            <ArrowLeft size={16} />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 md:py-12 z-10 max-w-6xl mx-auto w-full">
        
        {/* Header Title Section */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0c513f]/10 border border-[#0c513f]/15 text-[#0c513f] text-xs md:text-sm font-extrabold uppercase tracking-wider mb-4 shadow-sm">
            <Sparkles size={14} className="text-[#0c513f]" />
            <span>Rita Unified Access</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#0c513f] tracking-tighter-premium leading-none mb-4">
            Select Your Portal
          </h1>
          
          <p className="text-secondary-600 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Welcome to Rita. Choose your portal below to sign in, manage transactions, or oversee your business operations.
          </p>
        </div>

        {/* Portal Selection Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
          
          {/* Customer Portal Card */}
          <div className="group relative bg-white rounded-[2.5rem] p-8 md:p-10 border border-[#0c513f]/10 shadow-[0_20px_60px_-15px_rgba(12,81,63,0.08)] hover:shadow-[0_30px_70px_-10px_rgba(12,81,63,0.18)] transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between overflow-hidden">
            
            {/* Top Accent Gradient Bar */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0c513f] via-emerald-500 to-teal-400 opacity-80 group-hover:opacity-100 transition-opacity" />
            
            <div>
              {/* Header inside Card */}
              <div className="flex items-center justify-between mb-8">
                <div className="w-16 h-16 rounded-2xl bg-[#0c513f]/10 border border-[#0c513f]/15 flex items-center justify-center text-[#0c513f] group-hover:scale-110 group-hover:bg-[#0c513f] group-hover:text-white transition-all duration-500 shadow-sm">
                  <User size={32} />
                </div>
                <span className="px-3.5 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-bold tracking-wide">
                  For Users & Shoppers
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0c513f] group-hover:text-emerald-800 transition-colors tracking-tight mb-3">
                Customer Portal
              </h2>
              
              <p className="text-secondary-600 text-sm sm:text-base leading-relaxed mb-6 font-medium">
                Log in to manage your digital wallet, scan & pay seamlessly with QR codes, explore verified local merchants, and track cashbacks.
              </p>

              {/* Feature Highlights Badges */}
              <div className="grid grid-cols-2 gap-2.5 mb-8">
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[#faf8ed] border border-[#0c513f]/10 text-xs font-bold text-secondary-800">
                  <Zap size={15} className="text-amber-500 shrink-0" />
                  <span>Instant QR Pay</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[#faf8ed] border border-[#0c513f]/10 text-xs font-bold text-secondary-800">
                  <Wallet size={15} className="text-emerald-600 shrink-0" />
                  <span>Digital Wallet</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[#faf8ed] border border-[#0c513f]/10 text-xs font-bold text-secondary-800">
                  <Store size={15} className="text-blue-500 shrink-0" />
                  <span>Discover Stores</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-[#faf8ed] border border-[#0c513f]/10 text-xs font-bold text-secondary-800">
                  <ShieldCheck size={15} className="text-purple-600 shrink-0" />
                  <span>100% Secure</span>
                </div>
              </div>
            </div>

            {/* Action Buttons & Links */}
            <div className="space-y-3 pt-2">
              <a
                href="https://app.rita.ng"
                className="w-full bg-[#0c513f] hover:bg-[#073b2e] text-white py-4 px-6 rounded-2xl font-black text-base flex items-center justify-between shadow-[0_10px_25px_rgba(12,81,63,0.25)] group-hover:shadow-[0_15px_35px_rgba(12,81,63,0.35)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Launch Customer Portal</span>
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <ArrowRight size={18} />
                </div>
              </a>

              <div className="flex items-center justify-center text-xs text-secondary-500 font-semibold gap-1.5 pt-1">
                <span>New to Rita?</span>
                <a
                  href="https://app.rita.ng"
                  className="text-[#0c513f] hover:underline font-bold"
                >
                  Create an account in 1 minute
                </a>
              </div>
            </div>
          </div>

          {/* Service Provider Portal Card */}
          <div className="group relative bg-[#0c513f] text-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_20px_60px_-15px_rgba(12,81,63,0.3)] hover:shadow-[0_30px_70px_-10px_rgba(12,81,63,0.45)] transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between overflow-hidden">
            
            {/* Ambient inner card glow */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-yellow-400/5 rounded-full blur-2xl pointer-events-none" />

            <div>
              {/* Header inside Card */}
              <div className="flex items-center justify-between mb-8 relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-[#faf8ed] group-hover:scale-110 group-hover:bg-white group-hover:text-[#0c513f] transition-all duration-500 shadow-sm">
                  <Building2 size={32} />
                </div>
                <span className="px-3.5 py-1.5 rounded-full bg-emerald-950/60 text-emerald-300 border border-emerald-700/50 text-xs font-bold tracking-wide">
                  For Merchants & Partners
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#faf8ed] tracking-tight mb-3 relative z-10">
                Service Provider Portal
              </h2>
              
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 font-medium relative z-10">
                Log in to manage your digital store, upload KYC credentials, manage field offerings, track real-time sales, and settle wallet payouts.
              </p>

              {/* Feature Highlights Badges */}
              <div className="grid grid-cols-2 gap-2.5 mb-8 relative z-10">
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/10 border border-white/10 text-xs font-bold text-white/95 backdrop-blur-sm">
                  <TrendingUp size={15} className="text-yellow-400 shrink-0" />
                  <span>Sales & Analytics</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/10 border border-white/10 text-xs font-bold text-white/95 backdrop-blur-sm">
                  <Zap size={15} className="text-emerald-300 shrink-0" />
                  <span>Instant Payouts</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/10 border border-white/10 text-xs font-bold text-white/95 backdrop-blur-sm">
                  <CheckCircle2 size={15} className="text-teal-300 shrink-0" />
                  <span>KYC Verified</span>
                </div>
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/10 border border-white/10 text-xs font-bold text-white/95 backdrop-blur-sm">
                  <QrCode size={15} className="text-pink-300 shrink-0" />
                  <span>QR Terminal</span>
                </div>
              </div>
            </div>

            {/* Action Buttons & Links */}
            <div className="space-y-3 pt-2 relative z-10">
              <a
                href="https://provider.rita.ng"
                className="w-full bg-[#faf8ed] hover:bg-white text-[#0c513f] py-4 px-6 rounded-2xl font-black text-base flex items-center justify-between shadow-[0_10px_25px_rgba(0,0,0,0.2)] group-hover:shadow-[0_15px_35px_rgba(0,0,0,0.3)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Launch Provider Portal</span>
                <div className="w-9 h-9 rounded-xl bg-[#0c513f]/10 flex items-center justify-center group-hover:translate-x-1 transition-transform text-[#0c513f]">
                  <ArrowRight size={18} />
                </div>
              </a>

              <div className="flex items-center justify-center text-xs text-white/70 font-semibold gap-1.5 pt-1">
                <span>Want to accept Rita at your store?</span>
                <a
                  href="https://provider.rita.ng"
                  className="text-white hover:text-emerald-300 underline font-bold"
                >
                  Register your business
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Administrative Portal Secondary Link */}
        <div className="mt-12 w-full max-w-4xl bg-white/70 backdrop-blur-md border border-[#0c513f]/10 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="w-10 h-10 rounded-xl bg-[#0c513f]/10 text-[#0c513f] flex items-center justify-center shrink-0">
              <Shield size={20} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-[#0c513f]">Administrative & Staff Access</h4>
              <p className="text-xs text-secondary-500">Authorized personnel only. Access system governance, audit logs, and master controls.</p>
            </div>
          </div>

          <Link
            to="/admin/login"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary-900 hover:bg-black text-white text-xs font-bold transition-all duration-300 hover:scale-105 active:scale-95 shadow-sm"
          >
            <Lock size={14} />
            <span>Admin Login</span>
          </Link>
        </div>

        {/* Security & Guarantee Strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl text-center">
          <div className="p-3 rounded-2xl bg-white/50 border border-secondary-200/50">
            <ShieldCheck size={20} className="mx-auto text-[#0c513f] mb-1" />
            <p className="text-[11px] font-bold text-secondary-800">Bank-Grade Security</p>
            <p className="text-[10px] text-secondary-500">End-to-End Encryption</p>
          </div>
          <div className="p-3 rounded-2xl bg-white/50 border border-secondary-200/50">
            <Zap size={20} className="mx-auto text-amber-500 mb-1" />
            <p className="text-[11px] font-bold text-secondary-800">Zero Latency</p>
            <p className="text-[10px] text-secondary-500">Instant QR Confirmations</p>
          </div>
          <div className="p-3 rounded-2xl bg-white/50 border border-secondary-200/50">
            <CheckCircle2 size={20} className="mx-auto text-emerald-600 mb-1" />
            <p className="text-[11px] font-bold text-secondary-800">NDPR & PCI Ready</p>
            <p className="text-[10px] text-secondary-500">Regulated & Compliant</p>
          </div>
          <div className="p-3 rounded-2xl bg-white/50 border border-secondary-200/50">
            <Headphones size={20} className="mx-auto text-blue-500 mb-1" />
            <p className="text-[11px] font-bold text-secondary-800">24/7 Live Support</p>
            <p className="text-[10px] text-secondary-500">Dedicated Help Desk</p>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="w-full max-w-7xl mx-auto px-6 py-6 border-t border-[#0c513f]/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-secondary-500 font-medium z-10">
        <p>© {new Date().getFullYear()} Rita Platform. All rights reserved.</p>
        <div className="flex items-center space-x-6">
          <Link to="/privacy-policy" className="hover:text-[#0c513f] transition-colors">Privacy Policy</Link>
          <Link to="/terms-of-service" className="hover:text-[#0c513f] transition-colors">Terms of Service</Link>
          <Link to="/contact" className="hover:text-[#0c513f] transition-colors">Contact Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default LoginPortal;
